
import { useState } from "react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useToast } from "@/components/ui/use-toast";
import { handleError } from "@/utils/error-handling";
import { validateCaptchaToken } from "@/utils/captcha";
import { validatePassword } from "@/utils/passwordUtils";

export const useEmailAuth = () => {
  const supabase = useSupabaseClient();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailAuth = async (
    e: React.FormEvent,
    isSignUp: boolean,
    email: string,
    password: string,
    captchaToken: string | null,
    recaptchaReset: () => void
  ) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate captcha for email-based authentication
      if (isSignUp && !await validateCaptchaToken(captchaToken)) {
        throw new Error("Please complete the captcha verification");
      }
      
      let result;
      
      if (isSignUp) {
        // Custom password validation - only performed on signup
        const passwordError = validatePassword(password);
        if (passwordError) {
          throw new Error(passwordError);
        }
        
        // Get the application base URL without any path or hash
        const redirectUrl = window.location.origin;
        console.log("Using redirect URL:", redirectUrl);
        
        // For signup, explicitly set redirectTo to just the origin
        result = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
          },
        });
      } else {
        result = await supabase.auth.signInWithPassword({
          email,
          password,
        });
      }
      
      const { error } = result;
      
      if (error) throw error;
      
      if (isSignUp && !result.data?.user?.identities?.length) {
        // User already exists
        setError("An account with this email already exists. Please sign in instead.");
        toast({
          title: "Sign up failed",
          description: "An account with this email already exists",
          variant: "destructive",
        });
      } else if (isSignUp) {
        toast({
          title: "Account created",
          description: "Please check your email to confirm your account",
        });
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      
      // Provide more user-friendly error messages
      let errorMessage = error.message || "Authentication failed";
      
      // Handle common auth errors with friendly messages
      if (error.message?.includes("Invalid login credentials")) {
        errorMessage = "Invalid email or password. Please try again.";
      } else if (error.message?.includes("Email not confirmed")) {
        errorMessage = "Please confirm your email address before signing in.";
      } else if (error.message?.includes("User already registered")) {
        errorMessage = "An account with this email already exists. Please sign in instead.";
      }
      
      setError(errorMessage);
      
      toast({
        title: isSignUp ? "Sign up failed" : "Login failed",
        description: errorMessage,
        variant: "destructive",
      });

      // Reset captcha on error during sign up
      if (isSignUp) {
        recaptchaReset();
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    setError,
    handleEmailAuth
  };
};
